// API-Funktionen für Rechnungs-Drafts
import axios from 'axios';
import { InvoiceData } from '../types/invoiceTypes';

// Basis-URL für API-Anfragen - Aus Umgebungsvariable oder mit korrektem Pfad als Fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

// ===== DRAFT MANAGEMENT FUNKTIONEN =====

/**
 * Speichert einen Rechnungs-Draft (ohne offizielle Rechnungsnummer).
 * @param invoiceData Die Rechnungsdaten als Draft.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit der Draft-ID.
 */
export const saveInvoiceDraft = async (invoiceData: InvoiceData, token: string): Promise<{ draftId: string }> => {
    const ENDPOINT = `${API_BASE_URL}/invoice-drafts`;
    
    try {
        console.log('Speichere Rechnungs-Draft:', ENDPOINT);
        
        const response = await axios.post(ENDPOINT, invoiceData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
        });

        // Backend sendet { success: true, data: { id: "...", ... } }
        const draftId = response.data?.data?.id || response.data.draftId || response.headers['x-created-draft-id'];
        
        if (!draftId) {
            console.error('Unexpected response format:', response.data);
            throw new Error('Keine Draft-ID vom Backend erhalten.');
        }

        return { draftId };
    } catch (error: any) {
        console.error('Fehler beim Speichern des Invoice-Drafts:', error);
        if (error.response) {
            const errorMessage = error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            throw new Error(errorMessage);
        } else if (error.request) {
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

/**
 * Generiert ein ZUGFeRD Rechnungs-Preview PDF mit embedded XML ohne offizielle Nummernvergabe.
 * @param invoiceData Die Rechnungsdaten.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit dem Preview PDF-Blob und der Draft-ID.
 */
export const generateZugferdPreview = async (invoiceData: InvoiceData, token: string): Promise<{ pdfBlob: Blob, draftId: string }> => {
    const ENDPOINT = `${API_BASE_URL}/generate-zugferd-preview`;
    
    try {
        console.log('Generiere ZUGFeRD-Preview:', ENDPOINT);
        
        const response = await axios.post(ENDPOINT, invoiceData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            },
            responseType: 'arraybuffer',
        });

        const draftId = response.headers['x-created-draft-id'];
        
        if (!draftId) {
            throw new Error('Keine Draft-ID vom Backend erhalten.');
        }

        const pdfBlob = new Blob([response.data], { 
            type: 'application/pdf' 
        });
        
        return { pdfBlob, draftId };
    } catch (error: any) {
        console.error('Fehler beim Generieren des ZUGFeRD-Preview:', error);
        if (error.response) {
            const errorMessage = error.response.data instanceof ArrayBuffer
                ? "Backend-Fehler: " + new TextDecoder().decode(error.response.data)
                : error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            
            throw new Error(errorMessage);
        } else if (error.request) {
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

/**
 * Generiert ein XRechnung-Preview XML ohne offizielle Nummernvergabe.
 * @param invoiceData Die Rechnungsdaten.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit dem Preview XML-Blob und der Draft-ID.
 */
export const generateXrechnungPreview = async (invoiceData: InvoiceData, token: string): Promise<{ xmlBlob: Blob, draftId: string }> => {
    const ENDPOINT = `${API_BASE_URL}/generate-xrechnung-preview`;
    
    try {
        console.log('Generiere XRechnung-Preview:', ENDPOINT);
        
        const response = await axios.post(ENDPOINT, invoiceData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/xml'
            },
            responseType: 'arraybuffer',
        });

        const draftId = response.headers['x-created-draft-id'];
        
        if (!draftId) {
            throw new Error('Keine Draft-ID vom Backend erhalten.');
        }

        const xmlBlob = new Blob([response.data], { 
            type: 'application/xml' 
        });
        
        return { xmlBlob, draftId };
    } catch (error: any) {
        console.error('Fehler beim Generieren des XRechnung-Preview:', error);
        if (error.response) {
            const errorMessage = error.response.data instanceof ArrayBuffer
                ? "Backend-Fehler: " + new TextDecoder().decode(error.response.data)
                : error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            
            throw new Error(errorMessage);
        } else if (error.request) {
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

/**
 * Ruft die Liste der Rechnungs-Drafts für den aktuellen Benutzer ab.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Eine Liste von Draft-Metadaten.
 */
export const listInvoiceDrafts = async (token: string): Promise<any[]> => {
    const ENDPOINT = `${API_BASE_URL}/invoice-drafts`;
    console.log(`Rufe Rechnungs-Drafts von ${ENDPOINT} ab`);
    
    try {
        const response = await axios.get(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000
        });
        
        // Backend sendet { success: true, data: [...] } - extrahiere das data Array
        if (response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
        } else {
            console.warn('Unexpected response format:', response.data);
            return []; // Fallback zu leerem Array
        } 
    } catch (error: any) {
        console.error("Fehler beim Abrufen der Rechnungs-Drafts:", error);
        if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
        }
        throw new Error('Rechnungs-Drafts konnten nicht geladen werden.');
    }
};

/**
 * Ruft die Detaildaten eines spezifischen Rechnungs-Drafts ab.
 * @param draftId Die ID des Drafts.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Die Draft-Daten.
 */
export const getInvoiceDraftDetails = async (draftId: string, token: string): Promise<any> => {
    const ENDPOINT = `${API_BASE_URL}/invoice-drafts/${draftId}`;
    console.log(`Rufe Rechnungs-Draft-Details von ${ENDPOINT} ab`);
    
    try {
        const response = await axios.get(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000
        });
        
        // Backend sendet { success: true, data: {...} } - extrahiere das data Objekt
        if (response.data.success && response.data.data) {
            return response.data.data;
        } else {
            console.warn('Unexpected response format:', response.data);
            return response.data; // Fallback
        }
    } catch (error: any) {
        console.error(`Fehler beim Abrufen der Rechnungs-Draft-Details für ID ${draftId}:`, error);
         if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
             if (error.response.status === 404) {
                 throw new Error('Rechnungs-Draft nicht gefunden oder Zugriff verweigert.');
             }
        }
        throw new Error('Rechnungs-Draft-Details konnten nicht geladen werden.');
    }
};



/**
 * Löscht einen Rechnungs-Draft.
 * @param draftId Die ID des zu löschenden Drafts.
 * @param token Firebase ID-Token für die Authentifizierung.
 */
export const deleteInvoiceDraft = async (draftId: string, token: string): Promise<void> => {
    const ENDPOINT = `${API_BASE_URL}/invoice-drafts/${draftId}`;
    console.log(`Lösche Rechnungs-Draft ${draftId} bei ${ENDPOINT}`);
    
    try {
        await axios.delete(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: 15000
        });
    } catch (error: any) {
        console.error(`Fehler beim Löschen des Rechnungs-Drafts ${draftId}:`, error);
        if (error.response && error.response.status !== 404) {
            throw new Error('Rechnungs-Draft konnte nicht gelöscht werden.');
        }
        // 404 ignorieren, da Draft bereits gelöscht ist
    }
};